/**
 * Lista de productos con barra de filtros y scroll infinito: la parte comun de
 * los resultados de busqueda, una categoria y una linea.
 *
 * Cada vista pone lo suyo alrededor (titulo, migas, aviso de correccion) y lo
 * que se muestra cuando no hay nada que listar (`vacio`): en la busqueda es la
 * salida a WhatsApp; en una categoria, que todavia no hay productos publicados.
 * Que los filtros dejen la lista en cero es otra cosa, y es igual en las tres:
 * no se deriva a WhatsApp, se ofrece sacar los filtros.
 *
 * Categoria y linea pasan `enlacePagina`: su listado tiene una URL por pagina
 * (`?pagina=`) con enlaces a la anterior y a la siguiente, para que Google
 * recorra todo el catalogo. La busqueda no se indexa y no lo necesita.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type {
  FacetasBusqueda,
  FiltrosBusqueda as Filtros,
  ListadoFiltrado,
} from '@elcarpintero/compartido';

import type { ListadoInfinito } from '../../hooks/use-listado-infinito';
import { filtrosActivos, sinFiltros } from '../../utilidades/filtros-busqueda';
import { conteoProductos } from '../../utilidades/formato';
import { FiltrosActivos } from '../administracion/FiltrosActivos';
import { FiltrosBusqueda } from '../busqueda/FiltrosBusqueda';
import { AvisoError, Cargando } from '../comunes/EstadoPeticion';
import { Icono } from '../comunes/Icono';
import { CargaSiguiente } from './CargaSiguiente';
import { GrillaProductos } from './GrillaProductos';

export interface ListadoFiltrableProps {
  listado: ListadoInfinito<ListadoFiltrado>;
  /** Las del ultimo listado visto (`useUltimoDe`): la barra no parpadea al filtrar. */
  facetas: FacetasBusqueda | null;
  filtros: Filtros;
  onFiltrar(filtros: Filtros): void;
  /** Titulo (oculto) de la grilla, el `<h2>` sobre las tarjetas. */
  titulo: string;
  /** Que se esta cargando, para el aviso: "los resultados", "los productos". */
  que: string;
  /** De donde son los productos, para "hay 310 productos en Bisagras". */
  donde: string;
  /** Lo que va cuando no hay nada antes de filtrar. */
  vacio: ReactNode;
  /** La URL de una pagina de este listado, en los que se indexan. */
  enlacePagina?: (pagina: number) => string;
}

export function ListadoFiltrable({
  listado,
  facetas,
  filtros,
  onFiltrar,
  titulo,
  que,
  donde,
  vacio,
  enlacePagina,
}: ListadoFiltrableProps) {
  const { estado, datos, error, quedanMas, ultimaPagina, cargandoMas, errorMas, cargarMas } =
    listado;
  const quitarFiltros = () => {
    onFiltrar(sinFiltros(filtros));
  };

  return (
    <>
      {facetas !== null ? (
        <>
          <FiltrosBusqueda filtros={filtros} facetas={facetas} onCambiar={onFiltrar} />
          <FiltrosActivos
            activos={filtrosActivos(filtros, facetas)}
            onCambiar={onFiltrar}
            onLimpiar={quitarFiltros}
          />
        </>
      ) : null}

      {estado === 'cargando' ? <Cargando que={que} pagina={datos === null} /> : null}
      {estado === 'error' ? <AvisoError error={error} /> : null}

      {datos !== null && datos.totalSinFiltros === 0 ? vacio : null}

      {datos !== null && datos.totalSinFiltros > 0 && datos.total === 0 ? (
        <div className="vacio">
          <span className="vacio-icono">
            <Icono nombre="lupaVacia" />
          </span>
          <h2>Ningún producto cumple con todos estos filtros</h2>
          <p>
            Prueba quitando alguno: hay {conteoProductos(datos.totalSinFiltros)} {donde}.
          </p>
          <button type="button" className="btn" onClick={quitarFiltros}>
            Quitar los filtros
          </button>
        </div>
      ) : null}

      {datos !== null && datos.total > 0 ? (
        <>
          {/* Quien entro por una pagina del medio (desde Google) puede volver
              a las anteriores. */}
          {enlacePagina !== undefined && datos.pagina > 1 ? (
            <p className="listado-anteriores">
              <Link className="btn" to={enlacePagina(datos.pagina - 1)} rel="prev">
                Ver productos anteriores
              </Link>
            </p>
          ) : null}
          <GrillaProductos productos={datos.productos} titulo={titulo} titula />
          <CargaSiguiente
            // Los de las paginas anteriores a la de entrada tambien cuentan.
            cargados={(datos.pagina - 1) * datos.porPagina + datos.productos.length}
            total={datos.total}
            quedanMas={quedanMas}
            cargando={cargandoMas}
            error={errorMas}
            cargarMas={cargarMas}
            enlaceSiguiente={enlacePagina?.(ultimaPagina + 1)}
          />
        </>
      ) : null}
    </>
  );
}
